import { useEffect, useState } from "react";
import { Button, Dropdown, Form, Modal, Spinner } from "react-bootstrap";
import { AiOutlineClose } from "react-icons/ai";
import { IoIosMore } from "react-icons/io";
import InputCurrency, { getBalanceInt } from "../../shared/InputCurrency";
import { GiFireShield } from "react-icons/gi";
import useWallet from "../../../hooks/useWallet";
import { UpsertWalletRequestDTO } from "../../../@types/Wallet";
import Swal from "sweetalert2";
import { projectPrimaryColor } from "../../../generalConfigs";

type Props = {
  show: boolean,
  onClickClose: CallableFunction,
  callOnEnd: CallableFunction
}
export default function ConfigureWallet({ show, onClickClose, callOnEnd }: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [balance, setBalance] = useState<string>("");
  const [balanceGoal, setBalanceGoal] = useState<string>("");
  const [tolerance, setTolerance] = useState<string>("");
  const [exchange, setExchange] = useState<string>("");

  const { getWalletData, upsertWalletData } = useWallet();

  function parseBalanceFormat(balance: number): string {
    return (balance / 100).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  }

  function parseTolerance(tolerance: string): string {
    if (tolerance === 'low') {
      return 'BAIXA';
    }

    return 'ALTA';
  }

  useEffect(() => {
    async function fillWalletData() {
      setIsLoading(true);
      const walletData = await getWalletData();

      if (walletData) {
        setBalance(parseBalanceFormat(walletData.balance));
        setBalanceGoal(parseBalanceFormat(walletData.balance_goal));
        setTolerance(walletData.tolerance);
        setExchange(walletData.exchange);
      }

      setIsLoading(false);
    }

    fillWalletData();
  }, []);

  async function handleSubmit(e: any) {
    e.preventDefault();

    if (getBalanceInt(balance) > 99999999 || getBalanceInt(balanceGoal) > 99999999) {
      Swal.fire({
        title: "Balanço muito alto!",
        text: 'Para balanços acima de 1 milhão, por favor, entre em contato conosco para assessoria de IA personalizada. Para continuar, reduza o balanço e o balanço meta.',
        icon: "info",
        confirmButtonColor: projectPrimaryColor,
        background: "var(--bg-1)",
        color: "var(--text-1)",
        position: 'top'
      });

      return;
    } else if (getBalanceInt(balance) >= getBalanceInt(balanceGoal)) {
      Swal.fire({
        title: "Sem metas?",
        text: 'Sua meta de balanço precisa ser mais alta que seu balanço atual.',
        icon: "info",
        confirmButtonColor: projectPrimaryColor,
        background: "var(--bg-1)",
        color: "var(--text-1)",
        position: 'top'
      });

      return;
    }

    const dto: UpsertWalletRequestDTO = {
      balance: getBalanceInt(balance),
      balance_goal: getBalanceInt(balanceGoal),
      exchange,
      tolerance,
      assets: []
    }

    setIsLoading(true);
    const isUpdated = await upsertWalletData(dto);

    if (isUpdated) {
      Swal.fire({
        title: `Dados atualizados.`,
        text: `Os dados foram atualizados com sucesso, agora nossa IA vai reanalisar sua carteira, clique no botão abaixo para fechar esta janela e continuar.`,
        icon: "success",
        confirmButtonColor: projectPrimaryColor,
        confirmButtonText: "Fechar",
        background: "var(--bg-1)",
        color: "var(--text-1)",
        position: 'top'
      }).then(async (result) => {
        if (result.isConfirmed) {
          callOnEnd();
        }
      });
    } else {
      Swal.fire({
        title: "Ops!",
        text: "Infelizmente, aconteceu um erro desconhecido, por favor, tente novamente.",
        icon: "error",
        confirmButtonColor: projectPrimaryColor,
        background: "var(--bg-1)",
        color: "var(--text-1)",
        position: 'top'
      });
    }

    setIsLoading(false);
  }

  const exchanges: { name: string, icon: string }[] = [
    { name: "BTG Pactual", icon: "btg-pactual.png" },
    { name: "Binance", icon: "binance.png" },
    { name: "Nu Invest", icon: "nu-invest.png" },
    { name: "Avenue", icon: "avenue.png" },
    { name: "Inter", icon: "inter.png" },
    { name: "XP", icon: "xp-invest.png" },
    { name: "Rico", icon: "rico.png" },
    { name: "Toro", icon: "toro.png" },
  ]

  return (
    <Modal show={show} onHide={() => onClickClose()}>
      <Modal.Header>
        <Modal.Title>Configurar carteira</Modal.Title>
        <AiOutlineClose
          onClick={() => onClickClose()}
          size={23}
          className="float-right cursor-pointer"
        />
      </Modal.Header>

      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Corretora</Form.Label>
            <Dropdown>
              <Dropdown.Toggle className="rounded-button wd-100p" variant="secondary">
                {exchange}
              </Dropdown.Toggle>

              <Dropdown.Menu style={{ backgroundColor: 'var(--bg-1)' }} className="wd-100p">
                {
                  exchanges.map((exchange, index) => (
                    <Dropdown.Item
                      key={index}
                      className="dropdown-item-selectable text-center cursor-pointer"
                      onClick={() => setExchange(exchange.name)}
                    >
                      <img
                        src={`/exchanges/${exchange.icon}`}
                        alt={exchange.name}
                        className="wd-30 ht-30 rounded-circle"
                      />
                      <span className="mg-l-10">{exchange.name}</span>
                    </Dropdown.Item>
                  ))
                }

                <Dropdown.Item
                  className="dropdown-item-selectable text-center cursor-pointer"
                  onClick={() => setExchange('Outros')}
                >
                  <IoIosMore size={30} />
                  <span className="mg-l-10">Outros</span>
                </Dropdown.Item>

              </Dropdown.Menu>
            </Dropdown>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Balanço atual</Form.Label>
            <InputCurrency value={balance} setValue={setBalance} />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Balanço meta</Form.Label>
            <InputCurrency value={balanceGoal} setValue={setBalanceGoal} />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Tolerância á perdas</Form.Label>
            <Dropdown>
              <Dropdown.Toggle className="rounded-button wd-100p" variant="secondary">
                {parseTolerance(tolerance)}
              </Dropdown.Toggle>

              <Dropdown.Menu style={{ backgroundColor: 'var(--bg-1)' }} className="wd-100p">
                <Dropdown.Item
                  className="dropdown-item-selectable text-center cursor-pointer"
                  onClick={() => setTolerance('high')}
                >
                  <GiFireShield color="var(--green)" size={30} />
                  <span className="mg-l-10">ALTA</span>
                </Dropdown.Item>

                <Dropdown.Item
                  className="dropdown-item-selectable text-center cursor-pointer"
                  onClick={() => setTolerance('low')}
                >
                  <GiFireShield color="var(--red)" size={30} />
                  <span className="mg-l-10">BAIXA</span>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Form.Group>

          <div
            className="d-flex justify-content-center pd-t-20"
            style={{ gap: 20 }}
          >
            {isLoading ? <Spinner animation="border" size="sm" />
              : <><Button
                variant="secondary"
                className="rounded-button"
                type="button"
                onClick={() => onClickClose()}
              >
                Cancelar
              </Button>
                <Button variant="primary" className="rounded-button" type="submit">
                  Atualizar
                </Button></>}

          </div>
        </Form>
      </Modal.Body>
    </Modal >
  );
}
